import {
    CorrelationId,
    Subscription,
    SubscriptionData,
    SubscriptionRequest,
} from '@glue42/bbg-market-data';
import { buildLogger } from '@instinet-analytics/core-lib';
import {
    DapiRequestManagerBase,
    DapiRequestManagerBaseOptions,
} from './DapiRequestManagerBase';
import { BloombergError } from '@glue42/bbg-market-data/dist/cjs/request/types/bloomberg-error';
import { SubscriptionError } from '@glue42/bbg-market-data/dist/cjs/request/types/subscription-request';
import { RequestStatus } from '@glue42/bbg-market-data/dist/cjs/request-status';
import { v4 as uuid } from 'uuid';
import {
    addRequest,
    recordMessage,
    recordStatus,
} from 'app/glue/bloomberg/store/bloombergSlice';

import { TickDataReceivedHandler } from 'marketData/ExternalMarketDataStrategy';
import { TickData } from 'marketData/store/marketDataSlice';
import { parseBloombergDateTime } from 'app/glue/bloomberg/requests/utils';
import {
    BloombergMarketDataRequest,
    MarketDataTickRequest,
} from 'marketData/MarketDataRequest';

interface BloombergRequestInfo {
    subscription: Subscription;
    request: SubscriptionRequest;
}

const requestType = 'TickDataRequest';

type Options = DapiRequestManagerBaseOptions & {
    onTickDataReceived: TickDataReceivedHandler;
};

export class DapiTickDataRequestManager extends DapiRequestManagerBase {
    protected requestInfo: BloombergRequestInfo | undefined;
    private readonly uid: string;
    private readonly onTickDataReceived;

    public constructor(
        public request: MarketDataTickRequest & BloombergMarketDataRequest,
        options: Options
    ) {
        const logger = buildLogger(
            'BloombergDapiTickDataRequestManager',
            options.loggingService
        );
        super(request, logger, options);

        this.uid = uuid();
        this.onTickDataReceived = options.onTickDataReceived;
    }

    public subscribe() {
        const subscriptionId = new CorrelationId(this.uid);

        const subscription: Subscription = {
            delayed: false,
            fields: [
                'BID',
                'ASK',
                'MID',
                'LAST_PRICE',
                'VOLUME',
                'VWAP',
                'HIGH',
                'LOW',
                'ADV',
            ],
            intervalInSeconds: 60,
            security: `${this.request.bbSymbol} Equity`,
            subscriptionId,
        };

        this.dispatch(
            addRequest({
                uid: this.uid,
                requestType,
                request: this.request,
                args: subscription,
            })
        );

        const glueRequest: SubscriptionRequest =
            this.mktDataApi.createMarketDataRequest([subscription]);

        this.logger.info('Sending request to Bloomberg', glueRequest);

        glueRequest.onData((r) => this.onDataReceived(subscriptionId, r));
        glueRequest.onFail((r) => this.onRequestFail(subscriptionId, r));
        glueRequest.onError((r) => this.onRequestError(subscriptionId, r));
        glueRequest.onStatus((r) => this.onRequestStatus(subscriptionId, r));

        this.requestInfo = {
            subscription,
            request: glueRequest,
        };

        glueRequest.open();

        return subscriptionId;
    }

    protected async closeRequest() {
        if (!this.requestInfo) {
            return;
        }

        await this.requestInfo.request.close();

        this.logger.debug(
            'Closed request ' + this.requestInfo.subscription.subscriptionId
        );
    }

    protected onDataReceived(
        subscriptionId: CorrelationId,
        [data]: Array<SubscriptionData>
    ) {
        this.dispatch(
            recordMessage({
                uid: this.uid,
                requestType,
                eventType: 'onDataReceived',
                response: data,
            })
        );

        const tickData: TickData = {
            symbol: this.request.symbol,
            time: parseBloombergDateTime(data.TIME),
            low: data.LOW,
            high: data.HIGH,
            ask: data.ASK,
            bid: data.BID,
            volume: data.VOLUME,
            lastPrice: data.LAST_PRICE,
        };
        this.onTickDataReceived([tickData]);
    }

    protected onRequestError(
        subscriptionId: CorrelationId,
        error: BloombergError | Error
    ) {
        this.logger.debug('onRequestError ' + subscriptionId, error);

        this.dispatch(
            recordMessage({
                uid: this.uid,
                requestType,
                eventType: 'onRequestError',
                response: error,
            })
        );
    }

    protected onRequestFail(
        subscriptionId: CorrelationId,
        errors: Array<SubscriptionError>
    ) {
        this.logger.debug('onRequestFail ' + subscriptionId, errors);

        this.dispatch(
            recordMessage({
                uid: this.uid,
                requestType,
                eventType: 'onRequestFail',
                response: errors,
            })
        );
    }

    protected onRequestStatus(
        subscriptionId: CorrelationId,
        status: RequestStatus
    ) {
        this.logger.debug('onRequestStatus ' + subscriptionId, status);

        this.dispatch(
            recordStatus({
                uid: this.uid,
                requestType,
                eventType: 'onRequestFail',
                status,
            })
        );
    }
}
